import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Footer } from "../../layouts/footer";
import config from '../../../config';

// A simple Message component to display success or error messages
const Message = ({ type, message, onClose }) => {
  if (!message) return null;

  const bgColor = type === "success" ? "bg-green-500" : "bg-red-500";
  const borderColor = type === "success" ? "border-green-600" : "border-red-600";

  return (
    <div className={`fixed top-4 left-1/2 -translate-x-1/2 p-4 rounded-md shadow-lg text-white font-medium flex items-center justify-between z-50 ${bgColor} border ${borderColor}`}>
      <span>{message}</span>
      <button onClick={onClose} className="ml-4 text-white hover:text-gray-100 focus:outline-none">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

const EditProduct = () => {
  const { id } = useParams(); // Get the product ID from the URL
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    bore: "",
    thick: "",
    thickness_tolerance: "",
    free_diameter: "",
    clearance_onbore: "",
    beam: "",
    lug: "",
    hole_diamter: "",
    ring_load_carrying_capacity: "",
    groove_diameter: "",
    groove_tolerance: "",
    groove_width: "",
    edge_margin: "",
    groove_load_capacity: "",
    catname: "",
    price_1_249: "",
    price_250_999: "",
    price_1000_4999: "",
    price_5000_9999: "",
    price_10000plus: ""
  });
  const [message, setMessage] = useState(null); // State for displaying messages
  const [messageType, setMessageType] = useState("success"); // State for message type (success/error)

  // Function to show a message
  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    // Automatically hide the message after 3 seconds
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  };

  useEffect(() => {
    // Fetch product data when the component mounts or ID changes
    axios
      .get(`${config.baseURL}/productsfrontend/${id}`)
      .then((response) => {
        // Ensure all fields are initialized, handling potential nulls/undefined from API
        const fetchedData = response.data;
        setFormData(prev => ({
          ...prev, // Keep existing defaults for fields not in fetchedData if any
          ...fetchedData,
          price_1_249: fetchedData.price_1_249 ? String(fetchedData.price_1_249) : "",
          price_250_999: fetchedData.price_250_999 ? String(fetchedData.price_250_999) : "",
          price_1000_4999: fetchedData.price_1000_4999 ? String(fetchedData.price_1000_4999) : "",
          price_5000_9999: fetchedData.price_5000_9999 ? String(fetchedData.price_5000_9999) : "",
          price_10000plus: fetchedData.price_10000plus ? String(fetchedData.price_10000plus) : "",
        }));
      })
      .catch((error) => {
        console.error("Error fetching product data:", error);
        showMessage("Failed to load product data.", "error");
      });
  }, [id]); // Dependency array: re-run effect if 'id' changes

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredFields = [
      "name", "bore", "thick", "thickness_tolerance", "free_diameter",
      "clearance_onbore", "beam", "lug", "hole_diamter", "ring_load_carrying_capacity",
      "groove_diameter", "groove_tolerance", "groove_width", "edge_margin",
      "groove_load_capacity", "catname"
    ];

    for (const field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        showMessage(`Please fill in the '${field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}' field.`, "error");
        return; // Stop submission if a required field is empty
      }
    }

    const optionalFields = [
      "price_1_249",
      "price_250_999",
      "price_1000_4999",
      "price_5000_9999",
      "price_10000plus"
    ];

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      // Only append if not an empty optional field, or if it's a required field
      if (!(optionalFields.includes(key) && (value === null || String(value).trim() === ""))) {
        formDataToSend.append(key, value);
      }
    });

    axios
      .put(`${config.baseURL}/updateproductsfrontend/${id}`, formDataToSend)
      .then((response) => {
        if (response.data.status === "success") {
          showMessage("Product updated successfully!", "success");
          navigate("/admin/products");
        } else {
          showMessage(response.data.message || "Something went wrong.", "error");
        }
      })
      .catch((error) => {
        console.error("Error updating product:", error);
        showMessage("Failed to update product. Please try again.", "error");
      });
  };

  // Define input fields dynamically to reduce repetition
  const productInfoFields = [
    { id: "catname", name: "catname", label: "Product Category", placeholder: "", readOnly: true, required: false },
    { id: "name", name: "name", label: "Product Name", placeholder: "e.g., Spring Washer M10", required: true },
    { id: "bore", name: "bore", label: "Bore", placeholder: "e.g., 10mm", required: false },
    { id: "thick", name: "thick", label: "Thick", placeholder: "e.g., 2.5mm", required: false },
    { id: "thickness_tolerance", name: "thickness_tolerance", label: "Thickness Tolerance", placeholder: "e.g., +/- 0.1mm", required: false },
    { id: "free_diameter", name: "free_diameter", label: "Free Diameter", placeholder: "e.g., 18mm", required: false },
    { id: "clearance_onbore", name: "clearance_onbore", label: "Clearance On Bore", placeholder: "e.g., 0.5mm", required: false },
    { id: "beam", name: "beam", label: "Beam", placeholder: "e.g., 5mm", required: false },
    { id: "lug", name: "lug", label: "Lug", placeholder: "e.g., 3mm", required: false },
    { id: "hole_diamter", name: "hole_diamter", label: "Hole Diameter", placeholder: "e.g., 10.2mm", required: false },
    { id: "ring_load_carrying_capacity", name: "ring_load_carrying_capacity", label: "Ring Load Carrying Capacity", placeholder: "e.g., 500 N", required: false },
    { id: "groove_diameter", name: "groove_diameter", label: "Groove Diameter", placeholder: "e.g., 15mm", required: false },
    { id: "groove_tolerance", name: "groove_tolerance", label: "Groove Tolerance", placeholder: "e.g., +/- 0.05mm", required: false },
    { id: "groove_width", name: "groove_width", label: "Groove Width", placeholder: "e.g., 3.2mm", required: false },
    { id: "edge_margin", name: "edge_margin", label: "Edge Margin", placeholder: "e.g., 1.5mm", required: false },
    { id: "groove_load_capacity", name: "groove_load_capacity", label: "Groove Load Capacity", placeholder: "e.g., 300 N", required: false },
  ];

  const pricingFields = [
    { id: "price_1_249", name: "price_1_249", label: "Price (1 to 249 units)", placeholder: "e.g., 0.50" },
    { id: "price_250_999", name: "price_250_999", label: "Price (250 to 999 units)", placeholder: "e.g., 0.45" },
    { id: "price_1000_4999", name: "price_1000_4999", label: "Price (1000 to 4999 units)", placeholder: "e.g., 0.40" },
    { id: "price_5000_9999", name: "price_5000_9999", label: "Price (5000 to 9999 units)", placeholder: "e.g., 0.35" },
    { id: "price_10000plus", name: "price_10000plus", label: "Price (10000+ units)", placeholder: "e.g., 0.30" },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8">
      {/* Message component for alerts */}
      <Message type={messageType} message={message} onClose={() => setMessage(null)} />

      <div className="w-full max-w-4xl bg-gray-800 rounded-lg shadow-xl overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-700">
          <p className="text-2xl font-bold text-white">Edit Product</p>
          <p className="mt-1 text-gray-400">Modify the product details below.</p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Product Information Section */}
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                Product Details
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Update the core information and specifications of the product.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {productInfoFields.map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        placeholder={field.placeholder || `Enter ${field.label}...`}
                        value={formData[field.name] || ""} // Ensure value is not null/undefined
                        onChange={handleChange}
                        className={`block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 ${field.readOnly ? 'cursor-not-allowed' : ''}`}
                        readOnly={field.readOnly}
                        disabled={field.readOnly} // Added disabled attribute for readOnly fields
                        required={field.required}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Information Section */}
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                Pricing
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Adjust the pricing tiers for different quantities.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {pricingFields.map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label}
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        placeholder={field.placeholder}
                        value={formData[field.name] || ""} // Ensure value is not null/undefined
                        onChange={handleChange}
                        className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 pt-5">
              <button
                type="submit"
                className="w-full rounded-md bg-blue-600 px-4 py-3 text-lg font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors duration-200"
              >
                Update Product
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EditProduct;